import { useEffect, useMemo, useState } from 'react';
import { PRIVILEGES } from '@stayon/auth-core';
import { can, canManage, canViewGuestPii, hasAnyRole, ROLE_GROUPS, useAuth } from '@/shared/auth';
import { useObjectScope } from '@/shared/object-scope';
import { ManualBookingPanel } from '@/features/properties/manual-booking';
import { useResources } from '@/features/resources';
import { CalendarBody } from './CalendarBody';
import { CalendarFilterSheet } from './CalendarFilterSheet';
import { CalendarPageHeader } from './CalendarPageHeader';
import { CalendarReassignmentDialog } from './CalendarReassignmentDialog';
import { addIsoDays, localIsoDate } from './calendarHelpers';
import type { CalendarReassignmentRequest } from './calendarReassignment';
import type { CalendarMode } from './calendarSearch';
import { useApartmentCalendar } from './useApartmentCalendar';
import { useCalendarView } from './useCalendarView';

/**
 * Belegung (Board „Belegung“): Wald-Kopf mit Zeitraum und Modi, darunter die Karte mit Filtern und
 * dem Raster. Heute ist der Tag des Objekts – vom Server, bis dahin aus seiner Zeitzone.
 */
export function CalendarPage() {
  const { user } = useAuth();
  const { group } = useObjectScope();
  const editable = canManage(user);
  // Stellplätze, Ressourcen, Onboarding-Link: nur der Betrieb (die Endpunkte gehören Admin/Staff).
  const operations = hasAnyRole(user, ...ROLE_GROUPS.staff);
  // Wie der Server: Name und Telefon mit dem Kontakt-Recht, die E-Mail nur mit vollen Gastdaten.
  const guestAccess = {
    contact: can(user, PRIVILEGES.guestsOperationalContactView),
    email: canViewGuestPii(user),
  };
  const fallbackToday = localIsoDate(group?.timezone ?? 'Europe/Berlin');
  const view = useCalendarView(fallbackToday, operations);
  // Die Apartmentwahl gehört zum Objekt: nach einem Objektwechsel gilt wieder „alle“.
  const [picked, setPicked] = useState<{ groupId: string | null; ids: Set<string> | null }>({
    groupId: null,
    ids: null,
  });
  const selectedProperties = picked.groupId === (group?.id ?? null) ? picked.ids : null;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [manual, setManual] = useState<{ propertyId: string; date: string; name: string } | null>(
    null,
  );
  const [reassignment, setReassignment] = useState<CalendarReassignmentRequest | null>(null);

  const propertyIds = group?.apartments.map((apartment) => apartment.id) ?? [];
  const calendar = useApartmentCalendar(
    view.from,
    view.to,
    propertyIds,
    view.mode === 'apartments' && group !== null,
  );
  const resourceList = useResources(group?.id, operations);
  // Ein Ladefehler zeigt den Reiter trotzdem: der Bereich nennt dann selbst, was nicht geladen wurde.
  const showResources =
    resourceList.isError ||
    (resourceList.data?.resources.some((resource) => resource.isActive) ?? false);
  const today = calendar.data?.localToday ?? fallbackToday;
  const { search, focus, patch } = view;
  useEffect(() => {
    if (!search.focus && calendar.data?.localToday && calendar.data.localToday !== focus) {
      patch({ focus: calendar.data.localToday });
    }
  }, [calendar.data?.localToday, focus, patch, search.focus]);
  const dates = useMemo(
    () => Array.from({ length: view.dayCount }, (_, index) => addIsoDays(view.from, index)),
    [view.from, view.dayCount],
  );
  const modes: CalendarMode[] = [
    'apartments',
    ...(operations ? (['parking'] as const) : []),
    ...(operations && showResources ? (['resources'] as const) : []),
  ];
  const setMode = (mode: CalendarMode) =>
    view.patch({ mode: mode === 'apartments' ? undefined : mode });
  const toolbar = {
    propertyOptions: calendar.propertyOptions,
    selectedProperties,
    onSelectedPropertiesChange: (ids: Set<string> | null) =>
      setPicked({ groupId: group?.id ?? null, ids }),
    enabledChannels: view.enabledChannels,
    onToggleChannel: view.toggleChannel,
    onResetChannels: view.resetChannels,
    stayFilter: view.stayFilter,
    onStayFilterChange: (value: typeof view.stayFilter) =>
      view.patch({ stay: value === 'all' ? undefined : value }),
  };

  return (
    <>
      <CalendarPageHeader
        view={view}
        today={today}
        modes={modes}
        onModeChange={setMode}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <div className="relative z-10 -mt-[74px] flex flex-col gap-3.5 md:-mt-[84px] md:px-5">
        <CalendarBody
          view={view}
          group={group}
          calendar={calendar}
          dates={dates}
          today={today}
          toolbar={toolbar}
          editable={editable}
          operations={operations}
          guestAccess={guestAccess}
          onFreeDay={(propertyId, date, name) => setManual({ propertyId, date, name })}
          onReassignmentRequest={setReassignment}
        />
      </div>
      {view.mobile && (
        <CalendarFilterSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          view={view}
          today={today}
          modes={modes}
          onModeChange={setMode}
          toolbar={toolbar}
        />
      )}
      {manual && (
        <ManualBookingPanel
          open
          onOpenChange={(open) => !open && setManual(null)}
          apartment={{
            property: {
              id: manual.propertyId,
              name: manual.name,
              adminDisplayName: manual.name,
              currency: calendar.currencyForProperty(manual.propertyId),
            },
          }}
          prefillFrom={manual.date}
        />
      )}
      {reassignment && group && (
        <CalendarReassignmentDialog
          request={reassignment}
          apartments={group.apartments}
          calendar={calendar.data}
          onClose={() => setReassignment(null)}
        />
      )}
    </>
  );
}
